/**
 * Seen-state persistence for the tour and hints.
 *
 *  - Real environments: users/{uid}.onboarding via dotted-path updateDoc
 *    (nested merge — a hint write never clobbers tour fields). The rules arm
 *    onboardingValidIfTouched() validates the merged map.
 *  - Demo build (IS_DEMO): sessionStorage keyed by DEMO_STORAGE_KEY — the
 *    demo overlay denies every users write, and each new browser session
 *    should see the tour once.
 *
 * Every write is BEST-EFFORT: failures are logged, the local patch is kept,
 * nothing throws or blocks (shared-laptop classrooms, flaky Wi-Fi).
 *
 * Once-only guarantee (v3.80.1): an AUTO-start stamps tourVersion +
 * tourStartedAt before the first step renders (recordTourStarted), so a
 * teacher who closes the tab mid-tour, loses the network, or signs out is
 * never auto-toured again on any device — starting counts as seen. Done /
 * Skip / route-change still stamp their own timestamp afterwards; a replay
 * from Help writes nothing at start.
 */
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { IS_DEMO } from '../constants/featureFlags';
import { TOUR_VERSION, DEMO_STORAGE_KEY } from './constants';

function readDemo() {
  try {
    const raw = window.sessionStorage.getItem(DEMO_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (e) {
    return null;
  }
}

function writeDemo(next) {
  try {
    window.sessionStorage.setItem(DEMO_STORAGE_KEY, JSON.stringify(next));
  } catch (e) {
    // sessionStorage unavailable (private mode quirks) — the in-memory state still applies.
  }
}

/** Current seen-state: profile map (already loaded at boot) or the demo session record. */
export function readOnboardingState(user) {
  if (IS_DEMO) return readDemo();
  const o = user && user.profile && user.profile.onboarding;
  return o && typeof o === 'object' ? o : null;
}

function mergeLocal(user, patchFn) {
  if (IS_DEMO) {
    const next = patchFn(readDemo() || {});
    writeDemo(next);
    return next;
  }
  if (user && user.profile) {
    const next = patchFn(user.profile.onboarding && typeof user.profile.onboarding === 'object' ? user.profile.onboarding : {});
    user.profile.onboarding = next; // the app's convention (fontSizePercentage, padTierByClass)
    return next;
  }
  return patchFn({});
}

function warn(msg, err) {
  if (typeof console !== 'undefined' && console.warn) console.warn(`[onboarding] ${msg}`, err || '');
}

/**
 * Auto-start marker (never on replay): tourVersion + tourStartedAt, written
 * BEFORE the first step renders. Same best-effort contract; returns the new
 * local state so eligibility flips to "seen" in this session even if the
 * write is denied or offline.
 */
export function recordTourStarted(user) {
  const now = new Date();
  const next = mergeLocal(user, (prev) => ({ ...prev, tourVersion: TOUR_VERSION, tourStartedAt: IS_DEMO ? now.toISOString() : now }));
  if (!IS_DEMO && user && user.uid) {
    updateDoc(doc(db, 'users', user.uid), {
      'onboarding.tourVersion': TOUR_VERSION,
      'onboarding.tourStartedAt': serverTimestamp(),
    }).catch((err) => warn('tour start write failed; local state kept', err));
  }
  return next;
}

/**
 * Tour finished: 'completed' or 'skipped'. Both stamp tourVersion; the
 * matching timestamp is set; tourLastStep is the 0-based step the teacher
 * was on. Returns the new local state (Firestore write is fire-and-forget).
 */
export function recordTourResult(user, { outcome, lastStep }) {
  const tsKey = outcome === 'completed' ? 'tourCompletedAt' : 'tourSkippedAt';
  const step = Number.isInteger(lastStep) && lastStep >= 0 ? lastStep : 0;
  const now = new Date();
  const next = mergeLocal(user, (prev) => ({ ...prev, tourVersion: TOUR_VERSION, tourLastStep: step, [tsKey]: IS_DEMO ? now.toISOString() : now }));
  if (!IS_DEMO && user && user.uid) {
    updateDoc(doc(db, 'users', user.uid), {
      'onboarding.tourVersion': TOUR_VERSION,
      [`onboarding.${tsKey}`]: serverTimestamp(),
      'onboarding.tourLastStep': step,
    }).catch((err) => warn(`tour result write failed (${outcome}); local state kept`, err));
  }
  return next;
}

/** Hint shown: hints.<key>.seenAt. Same best-effort contract. */
export function recordHintSeen(user, key) {
  const now = new Date();
  const next = mergeLocal(user, (prev) => ({
    ...prev,
    hints: { ...(prev.hints || {}), [key]: { seenAt: IS_DEMO ? now.toISOString() : now } },
  }));
  if (!IS_DEMO && user && user.uid) {
    updateDoc(doc(db, 'users', user.uid), { [`onboarding.hints.${key}.seenAt`]: serverTimestamp() })
      .catch((err) => warn(`hint write failed (${key}); local state kept`, err));
  }
  return next;
}
